/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, OnInit, signal} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import template from './tier-priority-modal.component.html';
import {TierApiService} from './tier-api.service';
import {NetworkApiService} from '../network/network-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule],
  selector: 'adn-tier-priority-modal',
  template
})
export class TierPriorityModalComponent implements OnInit {
  /** Set via BsModalService initialState */
  inputNetwork: any;

  network: any;
  private readonly tiersState = signal<any[]>([]);
  private readonly resolvedState = signal(false);
  private readonly hasDisabledTiersState = signal(false);
  private readonly pendingState = signal(false);
  private readonly validationErrorState = signal(false);

  get tiers(): any[] { return this.tiersState(); }
  set tiers(value: any[]) { this.tiersState.set(value); }
  get resolved(): boolean { return this.resolvedState(); }
  set resolved(value: boolean) { this.resolvedState.set(value); }
  get hasDisabledTiers(): boolean { return this.hasDisabledTiersState(); }
  set hasDisabledTiers(value: boolean) { this.hasDisabledTiersState.set(value); }
  get pending(): boolean { return this.pendingState(); }
  set pending(value: boolean) { this.pendingState.set(value); }
  get validationError(): boolean { return this.validationErrorState(); }
  set validationError(value: boolean) { this.validationErrorState.set(value); }

  readonly onSaved = new Subject<any>();

  constructor(
    public bsModalRef: BsModalRef,
    private readonly tierApi: TierApiService,
    private readonly networkApi: NetworkApiService
  ) {}

  ngOnInit(): void {
    // Clone so we only send tiers to the API, not the full network object
    this.network = {id: this.inputNetwork.id, tiers: _.cloneDeep(this.inputNetwork.tiers)};
    firstValueFrom(this.tierApi.listAll()).then((page: any) => {
        this.tiers = page.results;
        const indexedNetworkTiers = _.keyBy(this.network.tiers, 'id');
        _.forEach(this.tiers, (tier) => {
          tier.enabled = !!indexedNetworkTiers[tier.id];
          const idx = _.findIndex(this.network.tiers, ['id', tier.id]);
          tier.pos = tier.enabled ? (idx + 1) : 999;
        });
        this.refreshActiveNetworkData();
        this.resolved = true;
    });
  }

  get enabledTiers(): any[] {
    return _.orderBy(this.tiers.filter(t => t.enabled), 'pos');
  }

  get disabledTiers(): any[] {
    return _.orderBy(this.tiers.filter(t => !t.enabled), 'pos');
  }

  toggleEnable(tier: any): void {
    if (tier.enabled) {
      this.network.tiers.push(tier);
    } else {
      tier.pos = 999;
      this.network.tiers = _.reject(this.network.tiers, ['id', tier.id]);
    }
    this.refreshActiveNetworkData();
  }

  priority(idx: number, raise: boolean): void {
    const iLength = this.refreshActiveNetworkData();
    for (let i = 0; i < iLength; i++) {
      const tier = this.network.tiers[i];
      tier.pos = (i + 1) * 10;
      if (i === idx && raise) {
        tier.pos -= 11;
      } else if (i === idx && !raise) {
        tier.pos += 11;
      }
    }
    this.network.tiers = _.orderBy(this.network.tiers, 'pos', 'asc');
    const indexedNetworkTiers = _.keyBy(this.network.tiers, 'id');
    _.forEach(this.tiers, (tier) => {
      const networkTier = indexedNetworkTiers[tier.id];
      tier.pos = networkTier ? networkTier.pos : 999;
    });
  }

  save(): void {
    this.pending = true;
    this.validationError = false;
    firstValueFrom(this.networkApi.save(this.network)).then((updatedNetwork: any) => {
      this.onSaved.next(updatedNetwork);
      this.bsModalRef.hide();
    }, () => {
      this.validationError = true;
      this.pending = false;
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  private refreshActiveNetworkData(): number {
    const tierIds = _.map(this.tiers, 'id');
    this.hasDisabledTiers = !!_.find(this.tiers, ['enabled', false]);
    const activeNetworkTierLength = _.intersection(tierIds, _.map(this.network.tiers, 'id')).length;
    if (activeNetworkTierLength !== tierIds.length) {
      this.network.tiers = _.filter(this.network.tiers, (t: any) => tierIds.indexOf(t.id) > -1);
    }
    return activeNetworkTierLength;
  }
}
